import React, { useState } from "react";
import { Card, Row, Col, Table, Statistic, Tag, Typography } from "antd";
import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { ComboConfig, FormColumnConfig, MasterConfigs } from "../../../../interfaces/entity";
import MasterComponent from "../../../../components/Forms";
import { constants } from "../../../../constants/common";
import dayjs from "dayjs";
import moment from "moment";
import SmallProfileCard from "../../../../components/ContactInfo/SmallProfileCard";
import { formatPriceIndian } from "../../../../constants/functions";
const { Title } = Typography;

const SetHRATargets = () => {
    const [fiscalYear, setFiscalYear] = useState(null);
    const [targetType, setTargetType] = useState(null);

    const config: MasterConfigs = {
        api: () => `/hr-targets`,
        queryParams: {},
        isActionTab: true,
        getColumns: () => [
            {
                title: "HR Name", dataIndex: "hrName", key: "hrName", render: (text) => <SmallProfileCard
                    name={constants.GET_FULL_NAME(text)}
                    avatar={text?.avatar}
                    subTitle={text?.employeeId}
                />
            },
            {
                title: "Fiscal Year", dataIndex: "fiscalYear", key: "fiscalYear",
                render: (text) => text?.name,
                align: 'center'
            },
            {
                title: "Quarter", dataIndex: "quarter", key: "quarter", render: (text) => {
                    const start = moment(text?.startDate);
                    const end = moment(text?.endDate);
                    return (
                        <span>
                            {text?.name} - <span className="text-gray-500 text-sm">{start.format("MMM D")} - {end.format("MMM D")}</span>
                            {moment().isBetween(start, end) && <Tag color="green" bordered={false}>Current</Tag>}
                        </span>
                    );
                }
            },
            {
                title: 'Total Head Count', dataIndex: 'totalHeadCount', key: 'totalHeadCount',
                align: 'center',
                render: (text, records) => records?.recruitment?.reduce((acc, item) => acc + (item.hireTarget || 0), 0) || '-'
            },
            {
                title: 'Total Budget (₹)', dataIndex: 'budget', key: 'budget',
                align: 'center',
                render: (text, records) => {
                    const recruitmentBudget = records?.recruitment?.reduce((acc, item) => acc + (item.budget || 0), 0);
                    const engagementBudget = records?.engagement?.reduce((acc, item) => acc + (item.budgetAllocated || 0), 0);
                    const trainingBudget = records?.training?.reduce((acc, item) => acc + (item.budgetAllocated || 0), 0);
                    const complianceBudget = records?.compliance?.reduce((acc, item) => acc + (item.budgetAllocated || 0), 0);
                    const total = recruitmentBudget + engagementBudget + trainingBudget + complianceBudget;
                    return total ? `₹ ${formatPriceIndian(total)}` : '-';
                }
            },
            { title: "Created At", dataIndex: "createdAt", key: "createdAt", render: (text) => moment(text).format("LLL") }
        ],
        onDataLoad: (data) => {
            if (data) {
                setFiscalYear(data.fiscalYear);
                setTargetType(data.targetType);
            }
        },
        getFormColumns: () => [
            {
                label: "HR Name",
                name: "hrName",
                type: 'select',
                isCombo: true,
                rules: [{ required: true, message: "Please select a HR!" }],
            },
            {
                label: "Fiscal Year",
                name: "fiscalYear",
                type: 'select',
                isCombo: true,
                rules: [{ required: true, message: "Please select the fiscal year!" }],
                selectProps: {
                    showSearch: true,
                    allowClear: true,
                    onChange: (value) => {
                        setFiscalYear(value);
                    }
                }
            },
            {
                label: "Quarter",
                name: "quarter",
                type: 'select',
                isCombo: true,
                selectProps: {
                    showSearch: true,
                    allowClear: true,
                    disabled: !fiscalYear,
                },
                rules: [{ required: true, message: "Please select the quarter!" }],
            },
            {
                type: 'custom',
                formItem: () => <Title level={5} className="text-gray-500">Hire Target</Title>,
                span: 8
            },
            {
                name: "recruitment",
                type: 'form',
                span: 24,
                form: [
                    {
                        label: "Job Title",
                        name: "jobTitle",
                        type: 'select',
                        isCombo: true,
                        span: 8,
                        rules: [{ required: true, message: "Please select a job title!" }],
                    },
                    {
                        label: "Target head count",
                        name: "hireTarget",
                        type: 'number',
                        span: 8,
                        rules: [{ required: true, message: "Please enter the target head count!" }],
                    },
                    {
                        label: "Budget (₹)",
                        name: "budget",
                        type: 'number',
                        span: 8,
                        rules: [{ required: true, message: "Please enter the budget!" }],
                    }
                ]
            },
            {
                label: "Retention Target (%)",
                name: "retention",
                type: 'text',
                span: 8,
                rules: [{ required: true, message: "Please enter the retention target!" }]
            },
            {
                type: 'custom',
                formItem: () => <Title level={5} className="text-gray-500">Engagement</Title>,
                span: 24
            },
            {
                name: "engagement",
                type: 'form',
                span: 24,
                form: [
                    {
                        label: "Activities Planned",
                        name: "activitiesPlanned",
                        type: 'number',
                        span: 8,
                        rules: [{ required: true, message: "Please enter the activities planned!" }],
                    },
                    {
                        label: "Budget (₹)",
                        name: "budgetAllocated",
                        type: 'number',
                        span: 8,
                        rules: [{ required: true, message: "Please enter the budget allocated!" }],
                    }
                ]
            },
            {
                type: 'custom',
                formItem: () => <Title level={5} className="text-gray-500">Training</Title>,
                span: 24
            },
            {
                name: "training",
                type: 'form',
                span: 24,
                form: [
                    {
                        label: "Sessions Planned",
                        name: "sessionsPlanned",
                        type: 'number',
                        span: 8,
                        rules: [{ required: true, message: "Please enter the sessions planned!" }],
                    },
                    {
                        label: "Budget (₹)",
                        name: "budgetAllocated",
                        type: 'number',
                        span: 8,
                        rules: [{ required: true, message: "Please enter the budget allocated!" }],
                    }
                ]
            },
            {
                type: 'custom',
                formItem: () => <Title level={5} className="text-gray-500">Compliance</Title>,
                span: 24
            },
            {
                name: "compliance",
                type: 'form',
                span: 24,
                form: [
                    {
                        label: "Compliance Target",
                        name: "complianceTarget",
                        type: 'number',
                        span: 8,
                        rules: [{ required: true, message: "Please enter the compliance target!" }],
                    },
                    {
                        label: "Budget (₹)",
                        name: "budgetAllocated",
                        type: 'number',
                        span: 8,
                        rules: [{ required: true, message: "Please enter the budget allocated!" }],
                    }
                ]
            },
            {
                type: 'custom',
                formItem: () => <Title level={5} className="text-gray-500">Targets</Title>,
                span: 24
            },
            {
                name: "dailyTargets",
                type: 'form',
                span: 24,
                form: [
                    {
                        label: "Target Period",
                        name: "period",
                        type: 'select',
                        children: [{ name: 'Monthly', value: 'monthly' }, { name: 'Weekly', value: 'weekly' }, { name: 'Daily', value: 'daily' }],
                        span: 8,
                        rules: [{ required: true, message: "Please select a period!" }],
                    },
                    {
                        label: "Target For",
                        name: "targetFor",
                        type: 'select',
                        children: constants.TARGET_FOR,
                        span: 8,
                        rules: [{ required: true, message: "Please select a job title!" }],
                    },
                    {
                        label: "Target Value",
                        name: "targetValue",
                        type: 'number',
                        span: 8,
                        rules: [{ required: true, message: "Please enter the target value!" }],
                    }
                ]
            },
            {
                label: "Description",
                name: "description",
                type: 'textarea',
                span: 16,
            }
        ],
        combo: [
            {
                api: '/users',
                idKey: '_id',
                render: (text) => constants.GET_FULL_NAME(text),
                name: 'hrName'
            },
            fiscalYear && {
                api: `/quarters?fiscalYear=${fiscalYear}`,
                idKey: '_id',
                render: (quarter) => {
                    const today = moment();
                    const start = moment(quarter.startDate);
                    const end = moment(quarter.endDate);

                    let statusTag;
                    if (today.isBetween(start, end, null, '[]')) {
                        statusTag = <Tag color="green" bordered={false}>Current</Tag>;
                    } else if (today.isAfter(end)) {
                        statusTag = <Tag color="blue" bordered={false}>Passed</Tag>;
                    } else {
                        statusTag = <Tag color="orange" bordered={false}>Upcoming</Tag>;
                    }

                    return (
                        <span>
                            {quarter.name} - <span className="text-gray-500 text-sm">{start.format("MMM D")} - {end.format("MMM D")}</span> - {statusTag}
                        </span>
                    );
                },
                name: 'quarter',
                rerender: [fiscalYear]
            },
            {
                api: `/fiscal-year`,
                idKey: '_id',
                render: (text) => text.name,
                name: 'fiscalYear'
            },
            {
                name: 'jobTitle',
                api: '/job-titles',
                idKey: '_id',
                render: (text) => text.name,
            }
        ].filter(Boolean) as ComboConfig[],
        actions: [
            {
                actionType: "Add",
                name: "Add Target",
            }
        ]
    }

    return (
        <MasterComponent config={config} />
    );
};

export default SetHRATargets;
